// src/pages/admin/WelcomeMessagesManagement.js

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/authContext';
import Layout from '../Layout';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const WelcomeMessagesManagement = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageText, setMessageText] = useState('');

  useEffect(() => {
    if (isSuperAdmin()) {
      loadMessages();
    }
  }, []);

  const loadMessages = async () => {
    try {
      setLoading(true);
      let snapshot;
      
      try {
        // Try with orderBy first
        const q = query(
          collection(db, 'welcomeMessages'),
          orderBy('order', 'asc')
        );
        snapshot = await getDocs(q);
      } catch (error) {
        // If orderBy fails (index not ready), try without orderBy
        snapshot = await getDocs(collection(db, 'welcomeMessages'));
      }

      const loadedMessages = [];
      snapshot.docs.forEach((docSnapshot) => {
        loadedMessages.push({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        });
      });

      // Sort by order if available, otherwise by creation time
      loadedMessages.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return 0;
      });

      setMessages(loadedMessages);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load welcome messages');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleAddMessage = () => {
    setEditingMessage(null);
    setMessageText('');
    setShowEditModal(true);
  };

  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setMessageText(message.text || '');
    setShowEditModal(true);
  };

  const handleSaveMessage = async () => {
    if (!messageText.trim()) {
      toast.error('Message text cannot be empty');
      return;
    }

    try {
      const data = {
        text: messageText.trim(),
        active: true,
        updatedAt: serverTimestamp(),
      };

      if (editingMessage) {
        // Update existing message
        await updateDoc(doc(db, 'welcomeMessages', editingMessage.id), data);
        toast.success('Message updated successfully');
      } else {
        // Add new message
        const maxOrder = messages.length > 0
          ? Math.max(...messages.map(m => m.order || 0))
          : -1;
        data.order = maxOrder + 1;
        data.createdAt = serverTimestamp();
        
        await addDoc(collection(db, 'welcomeMessages'), data);
        toast.success('Message added successfully');
      }

      setShowEditModal(false);
      setEditingMessage(null);
      setMessageText('');
      loadMessages();
    } catch (error) {
      console.error('Error saving message:', error);
      toast.error('Failed to save message');
    }
  };

  const handleDeleteMessage = async (message) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'welcomeMessages', message.id));
      toast.success('Message deleted successfully');
      loadMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadMessages();
  };

  if (!isSuperAdmin()) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Access Denied</h2>
            <p className="mt-2 text-gray-600">
              You don't have permission to access this area.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Admin
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Welcome Messages</h1>
          <p className="mt-2 text-gray-600">
            Manage inspirational quotes shown on the home screen
          </p>
        </div>

        {/* Add Button */}
        <div className="mb-6">
          <button
            onClick={handleAddMessage}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add New Message
          </button>
        </div>

        {/* Messages List */}
        {loading && messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-4 text-gray-600">Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-600">No messages yet. Add your first message above.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {messages.map((message) => (
                <li key={message.id} className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-500 mr-2">
                          #{message.order !== undefined ? message.order + 1 : '?'}
                        </span>
                        {message.active === false && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-gray-900">{message.text}</p>
                    </div>
                    <div className="ml-4 flex items-center space-x-2">
                      <button
                        onClick={() => handleEditMessage(message)}
                        className="inline-flex items-center p-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteMessage(message)}
                        className="inline-flex items-center p-2 border border-gray-300 rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Edit/Add Modal */}
        {showEditModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                  {editingMessage ? 'Edit Message' : 'Add New Message'}
                </h3>
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message Text
                  </label>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    rows={4}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="Enter inspirational message..."
                  />
                </div>
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingMessage(null);
                      setMessageText('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveMessage}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {editingMessage ? 'Update' : 'Add'} Message
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default WelcomeMessagesManagement;
